import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Download, Upload, Plus, Trash2, Save, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API } from "@/context/AuthContext";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : null);

function SheetGrid({ sheet, columns, rows, onChange, focusKey }) {
  const set = (ri, ci, v) => onChange(rows.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? v : c)) : r)));
  const add = () => onChange([...rows, columns.map(() => "")]);
  const del = (ri) => onChange(rows.filter((_, i) => i !== ri));
  const focusRef = useRef(null);
  useEffect(() => { if (focusRef.current) { focusRef.current.scrollIntoView({ block: "center" }); focusRef.current.querySelector("input:nth-of-type(1)"); const inp = focusRef.current.querySelectorAll("input")[1]; if (inp) inp.focus(); } }, [focusKey, rows.length]);
  return (
    <div className="space-y-2" data-testid={`mappings-grid-${sheet}`}>
      <div className="overflow-x-auto max-h-[360px] overflow-y-auto rounded border border-slate-200">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-50"><tr>{columns.map((c) => <th key={c} className="text-left font-semibold text-slate-600 px-2 py-1.5 border-b border-slate-200">{c}</th>)}<th className="w-8 border-b border-slate-200"></th></tr></thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} ref={focusKey && String(r[0]) === String(focusKey) ? focusRef : null} className={`odd:bg-white even:bg-slate-50/50 ${focusKey && String(r[0]) === String(focusKey) ? "!bg-amber-50 ring-1 ring-inset ring-amber-300" : ""}`} data-testid={`mappings-row-${sheet}-${ri}`}>
                {columns.map((c, ci) => <td key={c} className="px-1 py-0.5"><input value={r[ci] ?? ""} onChange={(e) => set(ri, ci, e.target.value)} placeholder={c} data-testid={`mappings-cell-${sheet}-${ri}-${ci}`} className="w-full rounded border border-transparent bg-transparent px-1.5 py-1 text-xs hover:border-slate-300 focus:border-sky-500 focus:bg-white focus:outline-none" /></td>)}
                <td className="px-1"><button type="button" onClick={() => del(ri)} data-testid={`mappings-del-${sheet}-${ri}`} className="text-slate-300 hover:text-red-600" title="Remove row"><Trash2 className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length + 1} className="text-center text-slate-400 py-5">No rows yet — SAP names are used as-is until you add mappings here</td></tr>}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`mappings-add-${sheet}`} className="border-slate-300 h-7"><Plus className="h-3.5 w-3.5 mr-1" /> Add row</Button>
    </div>
  );
}

export function TallyMappings({ queue }) {
  const [data, setData] = useState(null);
  const [sheets, setSheets] = useState({});
  const [tab, setTab] = useState("GL_Ledger");
  const [focusKey, setFocusKey] = useState(null);
  const rootRef = useRef(null);

  // rows pushed from the "Unmapped ledgers" panel: append when the key is new, otherwise just jump to the existing row
  useEffect(() => {
    if (!queue?.items?.length) return;
    const first = queue.items[0];
    setSheets((cur) => {
      const next = { ...cur };
      queue.items.forEach((it) => {
        const rows = next[it.sheet] || [];
        if (it.row && !rows.some((r) => String(r[0]) === String(it.row[0]))) next[it.sheet] = [...rows, it.row];
      });
      return next;
    });
    setTab(first.sheet);
    setFocusKey(first.row ? String(first.row[0]) : String(first.key));
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [queue]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => axios.get(`${API}/tally-export/mappings`).then((r) => { setData(r.data); setSheets(r.data.sheets || {}); }).catch(() => toast.error("Could not load mappings"));
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => data && JSON.stringify(sheets) !== JSON.stringify(data.sheets), [sheets, data]);
  const schema = data?.schema || [];
  const cur = schema.find((s) => s.name === tab);

  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API}/tally-export/mappings`, { sheets }); toast.success(`Mappings saved · ${Object.values(r.data.counts).reduce((a, b) => a + b, 0)} rows`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const upload = async (file, sheet) => {
    if (!file) return;
    if (dirty && !window.confirm("You have unsaved edits in the grid — uploading will replace this sheet's rows with the file. Continue?")) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/tally-export/mappings/upload`, fd, { params: sheet ? { sheet } : {} });
      const n = (r.data.updated_sheets || []).map((k) => `${k.replace(/_/g, " ")} ${r.data.counts[k]}`).join(", ");
      toast.success(`Uploaded · ${n || "no rows"}`);
      if (!sheet && r.data.missing_sheets?.length) toast.info(`Sheets not in workbook were left unchanged: ${r.data.missing_sheets.join(", ")}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : e.response?.status === 401 ? "Session expired — please sign in again" : "Upload failed");
    } finally { setUploading(false); }
  };
  const download = async (sheet) => {
    try {
      const res = await axios.get(`${API}/tally-export/mappings/download`, { responseType: "blob", params: sheet ? { sheet } : {} });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = sheet ? `mappings_${sheet}.xlsx` : "mappings.xlsx"; a.click(); URL.revokeObjectURL(url);
      toast.success(sheet ? `${sheet.replace(/_/g, " ")} template downloaded` : "mappings.xlsx downloaded");
    } catch (e) { toast.error(e.response?.status === 401 ? "Session expired — please sign in again" : "Download failed"); }
  };

  return (
    <Card ref={rootRef} className="border-slate-200 shadow-sm" data-testid="tally-mappings">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div className="min-w-[220px] flex-1">
          <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2"><BookOpen className="h-4 w-4 text-sky-700" /> Ledger mappings (mappings.xlsx)</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">SAP G/L accounts, parties, material groups, TDS sections and house banks → the exact Tally ledger names. Edit here or upload the workbook; every export uses the saved version.{data?.updated_at && <span className="text-slate-400" data-testid="mappings-updated"> Last saved {fmtDate(data.updated_at)} by {data.updated_by}{data.filename ? ` (${data.filename})` : ""}.</span>}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => download()} data-testid="mappings-download" className="border-slate-300"><Download className="h-3.5 w-3.5 mr-1.5" /> Download full workbook</Button>
          <label htmlFor="mappings-upload" className="inline-flex items-center h-9 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50" data-testid="mappings-upload-label">{uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} Upload full workbook</label>
          <input id="mappings-upload" type="file" accept=".xlsx" className="sr-only" data-testid="mappings-upload" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid="mappings-save" className="bg-sky-700 hover:bg-sky-800 text-white">{saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} {dirty ? "Save changes" : "Saved"}</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data ? <p className="text-sm text-slate-400">Loading mappings…</p> : (
          <>
            <div className="inline-flex flex-wrap rounded-md border border-slate-200 p-0.5 bg-slate-50 text-xs" data-testid="mappings-tabs">
              {schema.map((s) => <button key={s.name} type="button" onClick={() => setTab(s.name)} data-testid={`mappings-tab-${s.name}`} className={`px-3 py-1 rounded flex items-center gap-1.5 ${tab === s.name ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`}>{s.name.replace(/_/g, " ")}<span className={`rounded-full px-1.5 text-[10px] ${tab === s.name ? "bg-white/20" : "bg-slate-200 text-slate-600"}`}>{(sheets[s.name] || []).length}</span></button>)}
            </div>
            {cur && (
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-xs text-slate-500 flex-1 min-w-[240px]" data-testid="mappings-help">{cur.help}</p>
                <div className="flex items-center gap-2 shrink-0" data-testid={`mappings-sheet-actions-${cur.name}`}>
                  <Button variant="outline" size="sm" onClick={() => download(cur.name)} data-testid={`mappings-sheet-download-${cur.name}`} className="border-sky-300 text-sky-800 h-8"><Download className="h-3.5 w-3.5 mr-1.5" /> Download {cur.name.replace(/_/g, " ")} template</Button>
                  <label htmlFor={`mappings-sheet-upload-${cur.name}`} className="inline-flex items-center h-8 rounded-md border border-sky-300 bg-white px-3 text-sm font-medium text-sky-800 cursor-pointer hover:bg-sky-50" data-testid={`mappings-sheet-upload-label-${cur.name}`}><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload filled {cur.name.replace(/_/g, " ")}</label>
                  <input id={`mappings-sheet-upload-${cur.name}`} type="file" accept=".xlsx" className="sr-only" data-testid={`mappings-sheet-upload-${cur.name}`} onChange={(e) => { upload(e.target.files?.[0], cur.name); e.target.value = ""; }} />
                </div>
              </div>
            )}
            {cur && <SheetGrid sheet={cur.name} columns={cur.columns} rows={sheets[cur.name] || []} focusKey={focusKey} onChange={(rows) => setSheets((s) => ({ ...s, [cur.name]: rows }))} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}
